import { AfterViewInit, Component, Input } from '@angular/core';
import cytoscape, { CytoscapeOptions } from 'cytoscape';
import dagre, { DagreLayoutOptions } from 'cytoscape-dagre';
import { SvgDesignService } from 'shared-lib';
import { ToastrService } from 'ngx-toastr';

cytoscape.use(dagre);

@Component({
  selector: 'app-preview-schematic-diagram',
  standalone: false,
  templateUrl: './preview-schematic-diagram.component.html',
  styleUrls: ['./preview-schematic-diagram.component.scss']
})
export class PreviewSchematicDiagramComponent implements AfterViewInit {
  @Input() substationList: any[] = [];

  cy: cytoscape.Core | null = null;

  constructor(private svgDesignService: SvgDesignService,
              private toastr: ToastrService){}
  
  ngAfterViewInit(): void {
    const cyJson = this.createDiagramJson();
    
    try
    {
      this.cy = cytoscape(cyJson);
    }
    catch
    {
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }

    // just in case of rare fail cases
    if (!this.cy){
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }
    
    let currentY = 0;
    const margin = 5; // space between parents

    this.cy.nodes('.substation').forEach(sub => {
      const children = sub.children();

      if (children.length === 0) {
        sub.remove();
        return;
      }

      const parentBB = sub.boundingBox({ includeLabels: true });
      const parentWidth = parentBB.w;
      const parentHeight = parentBB.h;

      // Because dagger doesn't know how to properly arrange compound nodes it views them being in the same layer so they seem arranged left to right
      // this dx makes them all be centered horizontally
      const parentCenterX = parentBB.x1 + parentWidth / 2;
      const dx = -parentCenterX / 2;

      // dy - to move parent nodes and their children so they are stacked from Top to Bottom with margin space between them
      const dy = currentY - parentBB.y1;

      this.cy!.batch(() => {
        sub.position({
          x: parentBB.x1 + dx + parentWidth / 2,
          y: parentBB.y1 + dy + parentHeight / 2 
        });

        children.forEach(ch => {
          const p = ch.position();
          ch.position({ x: p.x + dx, y: p.y + dy });
        });
      });

      currentY += parentHeight / 2 + margin;
    });

    // Second pass: horizontal alignment based on last node of previous parent and first node of current parent
    let prevLastChildX: number | null = null;

    this.cy.nodes('.substation').forEach(sub => {
      const children = sub.children().sort((a, b) => a.position('y') - b.position('y'));

      if (children.length === 0) return;

      const firstChild = children[0];       
      const lastChild = children[children.length - 1];

      const firstChildX = firstChild.position().x;
      const lastChildX = lastChild.position().x;

      let dx = 0;
      if (prevLastChildX !== null) {
        dx = prevLastChildX - firstChildX;
      }

      this.cy!.batch(() => {
        const parentPos = sub.position();
        sub.position({ x: parentPos.x, y: parentPos.y });

        children.forEach(ch => {
          const p = ch.position();
          ch.position({ x: p.x + dx, y: p.y });
        });
      });

      prevLastChildX = lastChildX + dx;
    });

    this.zoomAndPanVertically();
    this.centerViewHorizontally();
  }

  private createDiagramJson(): CytoscapeOptions {
    const nodes: any[] = [];
    const edges: any[] = [];
    const addedPairs = new Set<string>();

    // Create groups [parent nodes] and their connections
    for (const [index, sub] of this.substationList.entries()) {
      const substationId = `substation-${index}`;
      nodes.push({
        data: { id: substationId, label: sub.className },
        classes: 'substation'
      });

      if (index > 0) {
        const prevSubstationId = `substation-${index - 1}`;
        edges.push({
          data: {
            id: `edge-sub-${index - 1}-${index}`,
            source: prevSubstationId,
            target: substationId
          },
          classes: 'substation-edge'
        });
      }

      // Create nodes for each group
      for (const eq of sub.equipmentTemplates) {
        const icon = this.svgDesignService.getSvgDesign(eq.visualizationConfiguration?.symbol, eq.visualizationConfiguration?.color);
        const nodeId = `${substationId}-${eq.guid}`;

        nodes.push({
          data: { id: nodeId, label: eq.className, parent: substationId },
          style: {
            width: icon.width,
            height: icon.height,
            'background-image': icon.svg
              ? `data:image/svg+xml;base64,${btoa(String.fromCharCode(...new TextEncoder().encode(icon.svg)))}`
              : '',
            'background-fit': 'contain',
            'background-opacity': 0
          }
        });

        // Create edges for each group
        const { guid } = eq;
        const source = eq.connectivitySource?.guid;
        const target = eq.connectivityDestination?.guid;
        if (source && source.trim() !== '') {
          const sourceId = `${substationId}-${source}`;
          const targetId = `${substationId}-${guid}`;
          const pairKey = [sourceId, targetId].sort().join('-');
          if (!addedPairs.has(pairKey)) {
            addedPairs.add(pairKey);
            edges.push({ data: { id: pairKey, source: sourceId, target: targetId } });
          }
        }

        if (target && target.trim() !== '') {
          const sourceId = `${substationId}-${guid}`;
          const targetId = `${substationId}-${target}`;
          const pairKey = [sourceId, targetId].sort().join('-');
          if (!addedPairs.has(pairKey)) {
            addedPairs.add(pairKey);
            edges.push({ data: { id: pairKey, source: sourceId, target: targetId } });
          }
        }
      }
    }

    return {
      container: document.getElementById('cy-preview')!,
      elements: { nodes, edges },
      layout: {
        name: 'dagre',
        rankDir: 'TB',       // TB = Top → Bottom (good for electrical flow)
        nodeSep: 100,        // horizontal spacing between nodes
        rankSep: 80,        // vertical spacing between layers
        edgeSep: 80,         // space between edges
        fit: false,
        nodeDimensionsIncludeLabels: true
      } as DagreLayoutOptions,
      style: [
        {
          selector: 'node',
          style: {
            shape: 'rectangle',
            'border-width': 0,
            label: 'data(label)',
            'text-valign': 'center',
            'text-halign': 'right',
            color: '#000000',
            'text-margin-x': 5,
            'text-margin-y': 2
          }
        },
        {
          selector: 'node.substation',
          style: {
            'background-opacity': 0,
            'border-width': 2,
            'border-color': '#e4e4e4',
            'text-valign': 'top',
            'text-halign': 'center',
            'text-margin-y': -5,
            'padding': '20px',
            'font-size': '14px',
            color: '#848484',
          }
        },
        {
          selector: 'edge',
          style: {
            width: 2,
            'line-color': '#000000',
            'curve-style': 'bezier',
            // 'target-arrow-color': '#000000',
            // 'target-arrow-shape': 'triangle',
          }
        },
        {
          selector: 'edge.substation-edge',
          style: {
            'line-color': 'transparent',
            'width': 0
          }
        }
      ]
    };
  }

  zoomAndPanVertically(){
    const count = this.cy!.elements().length;

    // Start at 1× when small, gradually zoom out as count increases
    //  → 10 elems → ~1.0
    //  → 50 elems → ~0.8
    //  → 100 elems → ~0.6
    //  → 200+ elems → ~0.4 (minimum cap)
    const zoomFactor = Math.max(1 - (count / 250), 0.4);

    this.cy!.zoom(this.cy!.zoom() * zoomFactor);

    // Move viewport so the top of the graph is visible
    const pan = this.cy!.pan();
    const boundingBox = this.cy!.elements().boundingBox();

    // Move upward: we set pan.y so the top part is near the top of the viewport
    this.cy!.pan({
      x: pan.x,
      y: -boundingBox.y1 + 50 // adjust +50 for padding from the top
    });
  }

  centerViewHorizontally(){
    const bbox = this.cy!.elements().boundingBox();
    const container = this.cy!.container();

    if (container) {
      const containerWidth = container.clientWidth;
      const graphWidth = bbox.w;
      
      // Compute the panX offset needed to horizontally center the graph
      const panX = (containerWidth - graphWidth * this.cy!.zoom()) / 2 - bbox.x1 * this.cy!.zoom();

      // Keep the existing vertical pan (so we don’t move the diagram vertically)
      const currentPan = this.cy!.pan();

      this.cy!.pan({
        x: panX,
        y: currentPan.y
      });
    }
  }

  ngOnDestroy() {
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
  }
}